// frontend/src/components/cash-close/DeptGLBreakdown.js
//
// Renders the GL-account breakdown for a single department-month combo:
//   GL # | GL name | Actual (CAD) | Forecast (CAD) | Variance $ | Variance %
//
// Lets the user compare the cash close actuals with the MPD CAD financial plan
// at the GL-account level (the same level the plan is organized by). Shown
// inside the AdaptiveVarianceTable drilldown, ABOVE the bill-level lines.
import React from 'react';
import { useTheme, sans, mono, red, green } from '../../tokens';

const fmtCAD = (n) => {
  if (n == null) return '—';
  const sign = n < 0 ? '-' : '';
  return `${sign}$${Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

// Catch-all GL accounts that should normally stay near zero. If real cash
// lands here, the underlying bill was probably misclassified at the source
// (Sage) and the user should review + reclassify via the GL dropdown.
const CATCH_ALL_GLS = {
  '66070': 'Miscellaneous — review needed; usually means a bill was misclassified at the Sage AP level. Use the GL dropdown to reclassify to its proper expense GL.',
  '14300': 'Vendor Deposits — should clear to zero when goods are received. Bills sitting here long-term need investigation.',
  '66060': 'Office & Team Supplies — catch-all for office stuff; consider whether something belongs to a more specific GL.',
};

export default function DeptGLBreakdown({ actualByGL, forecastByGL, currency = 'CAD', fxRate = 1.37, coaMap = {}, onGLClick, selectedGL }) {
  const t = useTheme();
  const conv = (n) => currency === 'USD' && n != null ? n / fxRate : n;

  // Union of GL accounts present in either actuals or forecast.
  const glSet = new Set([
    ...Object.keys(actualByGL || {}),
    ...Object.keys(forecastByGL || {}),
  ]);
  if (glSet.size === 0) {
    return (
      <div style={{ padding: '12px 16px', fontSize: 11, color: t.textMuted, fontFamily: sans, fontStyle: 'italic' }}>
        No GL-level data for this department this month.
      </div>
    );
  }

  // Build rows sorted by absolute actual amount descending.
  const rows = Array.from(glSet).map(gl => {
    const actual = actualByGL?.[gl] ?? null;
    const fcEntry = forecastByGL?.[gl];
    const forecast = fcEntry?.forecastCAD ?? null;
    const name = fcEntry?.name || '';
    const varAmt = (actual != null && forecast != null) ? actual - forecast : null;
    // Pct uses |forecast| as denominator. Both actual + forecast are negative
    // (cash out) in CAD; positive variance = burned more than planned.
    const varPct = (actual != null && forecast != null && forecast !== 0)
      ? ((Math.abs(actual) - Math.abs(forecast)) / Math.abs(forecast)) * 100
      : null;
    return { gl, name, actual, forecast, varAmt, varPct };
  }).sort((a, b) => Math.abs(b.actual || 0) - Math.abs(a.actual || 0));

  const totalActual = rows.reduce((s, r) => s + (r.actual || 0), 0);
  const totalForecast = rows.reduce((s, r) => s + (r.forecast || 0), 0);
  const totalVarAmt = totalForecast !== 0 ? totalActual - totalForecast : null;
  const totalVarPct = totalForecast !== 0
    ? ((Math.abs(totalActual) - Math.abs(totalForecast)) / Math.abs(totalForecast)) * 100
    : null;

  return (
    <div style={{ padding: '12px 16px', background: t.bgSurface, borderBottom: `1px solid ${t.borderLight}` }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, fontFamily: sans, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
        By GL account
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sans, fontSize: 11 }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${t.borderLight}` }}>
            <th style={th(t)}>GL</th>
            <th style={th(t)}>Name</th>
            <th style={th(t, 'right')}>Actual</th>
            <th style={th(t, 'right')}>Forecast</th>
            <th style={th(t, 'right')}>Variance $</th>
            <th style={th(t, 'right')}>Variance %</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr
              key={r.gl}
              onClick={() => onGLClick && onGLClick(r.gl)}
              style={{
                borderBottom: `1px solid ${t.borderLight}`,
                cursor: onGLClick ? 'pointer' : 'default',
                background: selectedGL === r.gl ? '#FFF9E6' : 'transparent',
              }}
            >
              <td style={td(t, 'left', mono)}>
                {CATCH_ALL_GLS[r.gl] && (
                  <span
                    title={CATCH_ALL_GLS[r.gl]}
                    style={{
                      display: 'inline-block',
                      marginRight: 4,
                      color: '#D97706',
                      cursor: 'help',
                      fontSize: 12,
                    }}
                  >
                    ⚠
                  </span>
                )}
                {r.gl}
              </td>
              <td style={td(t)}>
                {r.name
                  || coaMap[r.gl]
                  || (r.forecast == null
                      ? <span style={{ color: t.textMuted, fontStyle: 'italic' }}>(not in plan)</span>
                      : '—')}
              </td>
              <td style={td(t, 'right', mono)}>{fmtCAD(conv(r.actual))}</td>
              <td style={td(t, 'right', mono, t.textSecondary)}>{fmtCAD(conv(r.forecast))}</td>
              <td style={td(t, 'right', mono, r.varPct == null ? t.textMuted : r.varPct > 0 ? red : green)}>
                {r.varAmt == null ? '—' : (r.varAmt < 0 ? '' : '+') + fmtCAD(conv(r.varAmt))}
              </td>
              <td style={td(t, 'right', mono, r.varPct == null ? t.textMuted : r.varPct > 0 ? red : green, 600)}>
                {r.varPct == null ? '—' : `${r.varPct > 0 ? '+' : ''}${r.varPct.toFixed(0)}%`}
              </td>
            </tr>
          ))}
          <tr style={{ borderTop: `2px solid ${t.borderMedium}`, background: t.bgCard }}>
            <td colSpan={2} style={{ ...td(t), fontWeight: 700 }}>Total</td>
            <td style={td(t, 'right', mono, t.textPrimary, 700)}>{fmtCAD(conv(totalActual))}</td>
            <td style={td(t, 'right', mono, t.textPrimary, 700)}>{fmtCAD(conv(totalForecast))}</td>
            <td style={td(t, 'right', mono, totalVarPct == null ? t.textMuted : totalVarPct > 0 ? red : green, 700)}>
              {totalVarAmt == null ? '—' : (totalVarAmt < 0 ? '' : '+') + fmtCAD(conv(totalVarAmt))}
            </td>
            <td style={td(t, 'right', mono, totalVarPct == null ? t.textMuted : totalVarPct > 0 ? red : green, 700)}>
              {totalVarPct == null ? '—' : `${totalVarPct > 0 ? '+' : ''}${totalVarPct.toFixed(0)}%`}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

const th = (t, align = 'left') => ({
  textAlign: align, padding: '6px 6px', fontSize: 9, fontWeight: 600, color: t.textMuted,
  textTransform: 'uppercase', letterSpacing: 0.5,
});
const td = (t, align = 'left', font = sans, color = null, weight = 400) => ({
  textAlign: align, padding: '6px 6px', color: color || t.textPrimary, fontFamily: font, fontWeight: weight,
});
